/**
 * Bryq assessments from the TA Action Board (Oct 2026).
 *
 * The board never emails a Bryq invite itself: the proxy adds the candidate to the chosen test's
 * Lever posting and moves them to "BRYQ Test", which is what Bryq's Lever integration reacts to —
 * the exact steps a recruiter does by hand in Lever. See proxy-server/lib/bryq.js.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

export type BryqKey = 'cognitive-de' | 'cognitive-en' | 'cultural-de' | 'cultural-en' | 'data-expert-en';

export interface BryqTest {
  key: BryqKey;
  label: string;
  /** False while the test has no open posting in Lever — shown, but not pickable. */
  available: boolean;
}

export interface BryqSent {
  key: BryqKey;
  label: string;
  sentAt: string | null;
  /** From Bryq's "Bryq score 58%" tag; null until the candidate finishes. */
  score: number | null;
}

export interface BryqOptions {
  tests: BryqTest[];
  suggested: BryqKey;
  sent: BryqSent[];
}

/** The five tests, as listed in Lever — the fallback for the "with invite" picker before a read. */
export const BRYQ_TESTS: BryqTest[] = [
  { key: 'cognitive-de', label: 'Bryq Cognitive Deutsch', available: true },
  { key: 'cognitive-en', label: 'Bryq Cognitive English', available: true },
  { key: 'cultural-de', label: 'Bryq Cultural Deutsch', available: true },
  { key: 'cultural-en', label: 'Bryq Cultural English', available: true },
  { key: 'data-expert-en', label: 'Bryq Data Expert English', available: true },
];

/** Cognitive, in the role's candidate language — the same default the server suggests. */
export function defaultBryqKey(language: 'de' | 'en' | undefined): BryqKey {
  return language === 'de' ? 'cognitive-de' : 'cognitive-en';
}

/** The tests, the suggestion and what this candidate already has. Read when the picker opens. */
export async function fetchBryqOptions(opportunityId: string): Promise<BryqOptions> {
  const res = await authenticatedFetch(
    `${BASE}/bryq-tests?opportunityId=${encodeURIComponent(opportunityId)}`,
    { cache: 'no-store' }
  );
  return (await res.json()) as BryqOptions;
}

/** The catalogue alone (any candidate's read carries it) — for the "with invite" pickers. */
export async function fetchBryqTests(opportunityId: string): Promise<BryqTest[]> {
  return (await fetchBryqOptions(opportunityId)).tests;
}

/**
 * Send one test. Rejects with the server's reason (already sent, not set up in Lever, …).
 * `source` doubles as the MODE written to Lever: 'alone' is the filter step (decide after the
 * result), 'with-invite' is in parallel to an interview.
 */
export async function sendBryq(input: {
  opportunityId: string;
  boardEmail: string;
  test: BryqKey;
  source: 'alone' | 'with-invite';
}): Promise<{ ok: true; test: { key: BryqKey; label: string } }> {
  const { source, ...rest } = input;
  const body = { ...rest, mode: source === 'with-invite' ? 'parallel' : 'filter' };
  try {
    const res = await authenticatedFetch(`${BASE}/bryq`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    void logAnalyticsEvent('bryq_sent', { test: input.test, source });
    return (await res.json()) as { ok: true; test: { key: BryqKey; label: string } };
  } catch (error) {
    void logAnalyticsEvent('bryq_send_failed', { test: input.test, source });
    throw error;
  }
}

/** The toast line for an invite that carried a Bryq step. Empty when none was asked for. */
export function bryqOutcomeText(bryq: { ok: boolean; label?: string; error?: string } | null | undefined): string {
  if (!bryq) return '';
  return bryq.ok ? ` ${bryq.label} sent too.` : ` ⚠️ Bryq was NOT sent: ${bryq.error}`;
}

/** One Bryq test a candidate has, as /bryq-status reports it. */
export interface BryqEntry {
  key: BryqKey;
  label: string;
  state: 'outstanding' | 'completed';
  score: number | null;
  sentAt: string | null;
  completedAt: string | null;
  /** filter = Bryq first, decide after; parallel = sent with an interview; null = sent in Lever by hand. */
  mode: 'filter' | 'parallel' | null;
}

export interface BryqBoardRow {
  opportunityId: string;
  name: string;
  stageName: string | null;
  leverUrl: string | null;
  postingId: string;
  roleTitle: string;
  test: BryqEntry;
  daysWaiting?: number;
}

export interface BryqBoardView {
  byOpportunity: Record<string, BryqEntry[]>;
  /** Filter-step results that are in and not acted on yet. */
  decide: BryqBoardRow[];
  /** Sent, not finished yet — oldest first. */
  outstanding: BryqBoardRow[];
}

/** The board's Bryq picture: chip states plus the two Overdue lists. */
export async function fetchBryqStatus(boardEmail: string): Promise<BryqBoardView> {
  const res = await authenticatedFetch(`${BASE}/bryq-status?email=${encodeURIComponent(boardEmail)}`, {
    cache: 'no-store',
  });
  return (await res.json()) as BryqBoardView;
}
